/**
 * Retire an orphaned ORDER-LEVEL external trip.
 *
 * Background (AOP-HTSH-06075): an order confirmed while single-leg gets its
 * external trip id stored on `orders.eapi_trip_id`. When that order later
 * becomes multi-leg (or otherwise grows per-leg trips), the per-leg sync
 * creates `-L1`/`-L2` trips but nothing ever retired the original order-level
 * trip — it stayed live upstream ("unplanned") with no leg pointing at it, and
 * `orders.eapi_trip_id` kept pointing at the dead link.
 *
 * This helper is called AFTER per-leg trips exist. It abandons the order-level
 * trip upstream when that trip is not one of the order's active leg trips, and
 * reports back so the caller can null `orders.eapi_trip_id` in the same save.
 *
 * Sync-then-Persist: the local pointer is only cleared when the upstream
 * abandon succeeded (or the trip was already terminal upstream).
 */
import { listActiveLegTripsForOrder } from "@/lib/leg-trip-sync";
import { isAlreadyCancelledUpstreamError } from "@/lib/eapi-cancel-utils";

export interface RetireOrphanResult {
  /** True when the upstream trip is retired and the caller should clear the column. */
  retired: boolean;
  /** Why nothing was done / what failed. */
  reason?:
    | "no_order_trip"
    | "no_leg_trips"
    | "still_attached_to_leg"
    | "upstream_failed";
  error?: string;
}

export async function retireOrphanedOrderLevelTrip(params: {
  orderId: string;
  orderLevelTripId: string | null | undefined;
  client: {
    cancelTrip: (id: string, reason?: string) => Promise<any>;
    abandonTrip?: (id: string, reason?: string) => Promise<any>;
  };
  reasonLabel?: string;
  /** Injected for tests. */
  listActiveLegTrips?: typeof listActiveLegTripsForOrder;
}): Promise<RetireOrphanResult> {
  const { orderId, orderLevelTripId, client } = params;
  if (!orderId || !orderLevelTripId) return { retired: false, reason: "no_order_trip" };

  const list = params.listActiveLegTrips ?? listActiveLegTripsForOrder;
  const active = await list(orderId);
  // Nothing per-leg yet — the order-level trip is still the order's only trip.
  if (!active || active.length === 0) return { retired: false, reason: "no_leg_trips" };
  if (active.some((t) => t.eapi_trip_id === orderLevelTripId)) {
    return { retired: false, reason: "still_attached_to_leg" };
  }

  const reasonLabel =
    params.reasonLabel ?? "Superseded by per-leg external trips (order-level trip orphaned)";
  try {
    if (client.abandonTrip) {
      await client.abandonTrip(orderLevelTripId, reasonLabel);
    } else {
      await client.cancelTrip(orderLevelTripId, reasonLabel);
    }
    console.log(
      `[order-level orphan] abandoned orphaned order trip ${orderLevelTripId} for order ${orderId}`,
    );
    return { retired: true };
  } catch (err: any) {
    if (isAlreadyCancelledUpstreamError(err)) {
      console.log(
        `[order-level orphan] order trip ${orderLevelTripId} already terminal upstream; clearing local pointer`,
      );
      return { retired: true };
    }
    console.warn(
      `[order-level orphan] failed to abandon ${orderLevelTripId} for order ${orderId}:`,
      err?.message || err,
    );
    return { retired: false, reason: "upstream_failed", error: err?.message ?? String(err) };
  }
}
